import { Trans } from '@lingui/react/macro';
import * as Tabs from '@radix-ui/react-tabs';
import { Button, cn, Dialog } from '@utima/ui';
import { Globe, Upload, Cloud, Rss } from 'lucide-react';
import { memo, useEffect, useRef, useState } from 'react';

import { useUser } from '@/hooks';
import { trpc } from '@/services/trpc';

import { AddDriveFolder } from './AddDriveFolder';
import { AddOnlineSource } from './AddOnlineSource';
import { AddRssFeed } from './AddRssFeed';
import { UploadDocuments } from './UploadDocuments';
import { AgentAccessSelector } from '../AgentAccessSelector';

type ModalTab = 'online' | 'upload' | 'drive' | 'rss';

function LimitDisplay({
  isLoading,
  totalProjectedCount,
  limit,
  projectedLimitReached,
}: {
  isLoading: boolean;
  totalProjectedCount: number;
  limit: number;
  projectedLimitReached: boolean;
}) {
  return (
    <div
      className={cn(
        'text-xs tabular-nums',
        projectedLimitReached && limit !== Infinity
          ? 'text-red-600 font-medium'
          : 'text-gray-600',
      )}
    >
      {isLoading ? (
        <Trans>Loading document limit...</Trans>
      ) : projectedLimitReached && limit !== Infinity ? (
        <Trans>
          {totalProjectedCount} / {limit} documents uploaded (Limit Reached)
        </Trans>
      ) : (
        <Trans>
          Documents: {totalProjectedCount} /{' '}
          {limit === Infinity ? 'Unlimited' : limit}
        </Trans>
      )}
    </div>
  );
}

export type AddDocumentsModalProps = {
  chatId?: string;
  allChats: Array<{
    id: string;
    name: string;
  }>;
  folderId?: string | null;
  defaultOpen?: boolean;
  defaultUrl?: string;
  /** Controlled open state — when provided, the built-in trigger is hidden. */
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
};

const TAB_TRIGGER_CLASS =
  'flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-2.5 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary';

export const AddDocumentsModal = memo(function AddDocumentsModal({
  chatId,
  allChats,
  folderId,
  defaultOpen = false,
  defaultUrl,
  externalOpen,
  onExternalOpenChange,
}: AddDocumentsModalProps) {
  const isControlled = externalOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled
    ? (v: boolean) => onExternalOpenChange?.(v)
    : setInternalOpen;

  const [activeTab, setActiveTab] = useState<ModalTab>('online');

  const { currentOrganizationId } = useUser();

  const [selectedChatIds, setSelectedChatIds] = useState<string[]>(
    chatId ? [chatId] : [],
  );
  const [availableForAllAgents, setAvailableForAllAgents] = useState(!chatId);
  const [pendingAddCount, setPendingAddCount] = useState(0);

  // Fetch Chat Data with specific document counts (chat-scoped context)
  const { data: chatData, isLoading: isLoadingChat } =
    trpc.chats.findOne.useQuery(
      {
        id: chatId!,
        populate: ['documentsCount', 'organization.tierConfig'],
      },
      { enabled: !!chatId },
    );

  // Use org ID from chat data when available, otherwise from user context
  const organizationId = chatData?.organization?.id ?? currentOrganizationId;

  // Fetch Subscription Data
  const { data: subscriptionData, isLoading: isLoadingSub } =
    trpc.subscriptions.getActive.useQuery(
      { organizationId: organizationId! },
      { enabled: !!organizationId },
    );

  const totalDocs = chatId
    ? Number(chatData?.documentsCount ?? 0)
    : Number(
        (subscriptionData as Record<string, unknown> | undefined)
          ?.currentDocumentCount ?? 0,
      );
  const totalProjectedCount =
    totalDocs + (activeTab === 'online' ? pendingAddCount : 0);
  const limit = subscriptionData?.maxDocuments ?? Infinity;
  const limitReached = totalDocs > limit;
  const projectedLimitReached = totalProjectedCount > limit;

  const showAgentAccess = !!chatId || allChats.length > 0;
  const availableAgents =
    chatData && !allChats.some(agent => agent.id === chatData.id)
      ? [{ id: chatData.id, name: chatData.name }, ...allChats]
      : allChats;

  const onlineTabRef = useRef<HTMLButtonElement>(null);
  const uploadTabRef = useRef<HTMLButtonElement>(null);
  const driveTabRef = useRef<HTMLButtonElement>(null);
  const rssTabRef = useRef<HTMLButtonElement>(null);
  const tabRefs: Record<ModalTab, React.RefObject<HTMLButtonElement | null>> = {
    online: onlineTabRef,
    upload: uploadTabRef,
    drive: driveTabRef,
    rss: rssTabRef,
  };

  useEffect(() => {
    if (defaultUrl) {
      setActiveTab('online');
    }
  }, [defaultUrl]);

  const handleOpenAutoFocus = (event: Event) => {
    event.preventDefault();
    tabRefs[activeTab]?.current?.focus();
  };

  const agentAccess = showAgentAccess ? (
    <div className='w-full min-w-0 pb-2 md:w-64 md:shrink-0'>
      <AgentAccessSelector
        compact
        availableForAllAgents={availableForAllAgents && showAgentAccess}
        setAvailableForAllAgents={setAvailableForAllAgents}
        selectedChatIds={selectedChatIds}
        setSelectedChatIds={setSelectedChatIds}
        availableAgents={availableAgents}
        currentChatId={chatId}
      />
    </div>
  ) : null;

  return (
    <Dialog.Root
      defaultOpen={isControlled ? undefined : defaultOpen}
      open={open}
      onOpenChange={isOpen => {
        setOpen(isOpen);

        if (isOpen) {
          setAvailableForAllAgents(!chatId);
          setSelectedChatIds(chatId ? [chatId] : []);
          setActiveTab('online');
        }
      }}
    >
      {!isControlled && (
        <Dialog.Trigger asChild>
          <Button data-cta='admin-add-documents-modal-trigger'>
            <Trans>Add resources</Trans>
          </Button>
        </Dialog.Trigger>
      )}
      <Dialog.Content
        className='flex max-h-[calc(100dvh-1rem)] min-h-0 w-[1000px] max-w-[calc(100vw-1rem)] flex-col gap-3 overflow-y-auto overflow-x-hidden p-4 sm:p-5'
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <Dialog.Header className='shrink-0 space-y-0 text-left'>
          <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-1 pr-7'>
            <Dialog.Title>
              <Trans>Add resources</Trans>
            </Dialog.Title>
            <LimitDisplay
              isLoading={isLoadingChat || isLoadingSub}
              totalProjectedCount={totalProjectedCount}
              limit={limit}
              projectedLimitReached={projectedLimitReached}
            />
          </div>
          <Dialog.Description className='sr-only'>
            <Trans>
              Add websites, documents, feeds, and Google Drive folders by URL,
              or upload files directly.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <Tabs.Root
          value={activeTab}
          onValueChange={value => {
            if (
              value === 'online' ||
              value === 'upload' ||
              value === 'drive' ||
              value === 'rss'
            ) {
              setActiveTab(value);
            }
          }}
          className='flex shrink-0 flex-col'
        >
          <div className='flex flex-col-reverse gap-x-4 gap-y-2 border-b border-border md:flex-row md:items-end md:justify-between'>
            <Tabs.List className='min-w-0'>
              <div className='flex gap-0.5 overflow-x-auto'>
                <Tabs.Trigger
                  value='online'
                  ref={onlineTabRef}
                  data-cta='admin-add-documents-modal-online-tab'
                  className={TAB_TRIGGER_CLASS}
                >
                  <Globe className='size-4' />
                  <Trans>Web</Trans>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value='upload'
                  ref={uploadTabRef}
                  data-cta='admin-add-documents-modal-upload-tab'
                  className={TAB_TRIGGER_CLASS}
                >
                  <Upload className='size-4' />
                  <Trans>Files</Trans>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value='drive'
                  ref={driveTabRef}
                  className={TAB_TRIGGER_CLASS}
                >
                  <Cloud className='size-4' />
                  <Trans>Google Drive</Trans>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value='rss'
                  ref={rssTabRef}
                  className={TAB_TRIGGER_CLASS}
                >
                  <Rss className='size-4' />
                  <Trans>RSS</Trans>
                </Tabs.Trigger>
              </div>
            </Tabs.List>
            {agentAccess}
          </div>

          <Tabs.Content
            forceMount
            value='online'
            className='flex shrink-0 flex-col gap-3 pt-3 data-[state=inactive]:hidden'
          >
            <AddOnlineSource
              limitDisplay={
                <LimitDisplay
                  isLoading={isLoadingChat || isLoadingSub}
                  totalProjectedCount={totalProjectedCount}
                  limit={limit}
                  projectedLimitReached={projectedLimitReached}
                />
              }
              chatIds={selectedChatIds}
              availableForAllAgents={availableForAllAgents && showAgentAccess}
              setOpen={setOpen}
              defaultUrl={defaultUrl}
              limitReached={limitReached}
              projectedLimitReached={projectedLimitReached}
              onPendingCountChange={setPendingAddCount}
              folderId={folderId}
            />
          </Tabs.Content>

          <Tabs.Content
            forceMount
            value='upload'
            className='flex shrink-0 flex-col py-4 data-[state=inactive]:hidden'
          >
            {open && (
              <UploadDocuments
                chatIds={selectedChatIds}
                availableForAllAgents={availableForAllAgents && showAgentAccess}
                limitReached={limitReached}
                projectedLimitReached={projectedLimitReached}
                onFilesDropped={() => setActiveTab('upload')}
                folderId={folderId}
              />
            )}
          </Tabs.Content>
          <Tabs.Content
            forceMount
            value='drive'
            className='py-4 data-[state=inactive]:hidden'
          >
            <AddDriveFolder
              chatIds={selectedChatIds}
              availableForAllAgents={availableForAllAgents && showAgentAccess}
              setOpen={setOpen}
              limitReached={limitReached}
              folderId={folderId}
            />
          </Tabs.Content>
          <Tabs.Content
            forceMount
            value='rss'
            className='py-4 data-[state=inactive]:hidden'
          >
            <AddRssFeed
              projectedLimitReached={projectedLimitReached}
              chatIds={selectedChatIds}
              availableForAllAgents={availableForAllAgents && showAgentAccess}
              setOpen={setOpen}
              limitReached={limitReached}
              folderId={folderId}
            />
          </Tabs.Content>
        </Tabs.Root>
      </Dialog.Content>
    </Dialog.Root>
  );
});
